import { readApiErrorMessage } from '@/core/api';
import { isUnregisteredError } from '@/core/authErrors';
import { auth } from './firebase';

const SESSION_MINT_RETRY_ATTEMPTS = 12;
const SESSION_MINT_RETRY_DELAY_MS = 150;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Mint an org-scoped Firebase session cookie for media GET routes.
 *
 * Retries briefly when Datastore email/membership lookup lags right after ``/setup``.
 */
export async function mintOrgSession(orgId: string | number): Promise<void> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }
  const token = await user.getIdToken();

  let lastError: unknown;
  for (let attempt = 0; attempt < SESSION_MINT_RETRY_ATTEMPTS; attempt += 1) {
    const res = await fetch(`/api/v1/organizations/${orgId}/auth/session`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });
    if (res.ok) {
      return;
    }
    const message = await readApiErrorMessage(res);
    lastError = new Error(message);
    if (!isUnregisteredError(message) || attempt >= SESSION_MINT_RETRY_ATTEMPTS - 1) {
      throw lastError;
    }
    await sleep(SESSION_MINT_RETRY_DELAY_MS);
  }
}

/** Clear the org-scoped session cookie (logout). */
export async function clearOrgSession(orgId: string | number): Promise<void> {
  const user = auth.currentUser;
  if (!user) return;
  const token = await user.getIdToken();
  await fetch(`/api/v1/organizations/${orgId}/auth/session`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  });
}

/** Clear session cookies for every org the user can access. */
export async function clearAllOrgSessions(orgIds: Array<string | number>): Promise<void> {
  await Promise.all(orgIds.map((id) => clearOrgSession(id)));
}

/** Mint session cookies for every accessible org (D21 — eager login mint). */
export async function mintAllOrgSessions(
  orgIds: Array<string | number | null | undefined>,
): Promise<void> {
  const unique = [
    ...new Set(
      orgIds
        .filter((id): id is string | number => id != null && id !== '')
        .map((id) => String(id)),
    ),
  ];
  await Promise.all(unique.map((id) => mintOrgSession(id)));
}
